import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  adminPaymentsService,
  type AdminPaymentlineView,
  type AdminPaymentlinesQuery,
  type PaymentlineAuditView,
  type UpdateAdminPaymentlinePayload,
} from '../../services/admin-payments/admin-payments.service';
import {
  adminOrdersService,
  type AdminOrderMonitoringDetailView,
  type AdminOrdersQuery,
  type AdminOrdersPage,
  type AdminOrdersSortKey,
} from '../../services/admin-orders/admin-orders.service';
import {
  adminKitchenPrintsService,
  type AdminKitchenPrintLogView,
} from '../../services/admin-kitchen-prints/admin-kitchen-prints.service';
import {
  adminSessionsService,
  type AdminSessionAuditView,
  type AdminSessionsQuery,
} from '../../services/admin-sessions/admin-sessions.service';
import {
  adminKdsAuditService,
  type AdminKdsAuditView,
  type AdminKdsAuditQuery,
  type AdminKdsAuditSortKey,
  type KdsAuditHistoryView,
  type UpdateAdminKdsStatusPayload,
} from '../../services/admin-kds-audit/admin-kds-audit.service';
import {
  adminTransferAuditService,
} from '../../services/admin-transfer-audit/admin-transfer-audit.service';
import {
  adminMergeAuditService,
} from '../../services/admin-merge-audit/admin-merge-audit.service';
import {
  adminLogsService,
  type LogEntryView,
  type LogsQuery,
} from '../../services/admin-logs/admin-logs.service';
import {
  adminOntopoService,
  type OntopoCallLogView,
  type OntopoCallLogsQuery,
} from '../../services/admin-ontopo/admin-ontopo.service';
import {
  adminWoltService,
  type WoltCallLogView,
  type WoltCallLogsQuery,
} from '../../services/admin-wolt/admin-wolt.service';
import {
  adminTenbisService,
  type TenbisCallLogView,
  type TenbisCallLogsQuery,
} from '../../services/admin-tenbis/admin-tenbis.service';
import {
  adminCibusService,
  type CibusCallLogView,
  type CibusCallLogsQuery,
} from '../../services/admin-cibus/admin-cibus.service';
import type { AdminTransferAuditView, AdminMergeAuditView } from 'shared-models/browser';
import {
  adminApprovalRequestsService,
  type AdminApprovalRequestDetailView,
  type AdminApprovalRequestSearchField,
  type AdminApprovalRequestView,
  type AdminApprovalRequestsQuery,
} from '../../services/admin-approval-requests/admin-approval-requests.service';
import {
  adminRestigoService,
  type RestigoCallLogView,
  type RestigoCallLogsQuery,
} from '../../services/admin-restigo/admin-restigo.service';
import { useAdminApprovalRequestsEvents } from '../../contexts/AdminEventsProvider';
import {
  PAGE_SIZE,
  type AmountFilterMode,
  type DateFilterMode,
  type SortDirection,
  type SortKey,
} from './monitoring-page.constants';
import type { AdminSessionsSortKey } from '../../services/admin-sessions/admin-sessions.service';

// Composite key: combo-child rows key on selectionId, others on orderlineId (disjoint id-spaces).
const kdsRowKey = (row: AdminKdsAuditView): string =>
  row.selectionId != null ? `sel-${row.selectionId}` : `ol-${row.orderlineId}`;

const kdsRowsMatch = (a: AdminKdsAuditView, b: AdminKdsAuditView): boolean => kdsRowKey(a) === kdsRowKey(b);

// All MonitoringPage state, the server-side query, fetching and row-edit/audit handlers.
export const useMonitoringPage = () => {
  const { revision: approvalRequestsRevision } = useAdminApprovalRequestsEvents();
  const [activeSection, setActiveSection] = useState<'orders' | 'payments' | 'mergeLogs' | 'kitchenPrints' | 'sessions' | 'approvalRequests' | 'kdsAudit' | 'zcredit' | 'errors' | 'restigo' | 'ontopo' | 'wolt' | 'tenbis' | 'cibus'>('orders');
  const [ordersRows, setOrdersRows] = useState<AdminOrdersPage['rows']>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersFilterOptions, setOrdersFilterOptions] = useState<AdminOrdersPage['filterOptions']>({
    companies: [],
    sessions: [],
    statuses: ['open', 'closed'],
  });
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [hasLoadedOrders, setHasLoadedOrders] = useState(false);
  const [ordersSearch, setOrdersSearch] = useState('');
  const [ordersCompanyFilter, setOrdersCompanyFilter] = useState<string[]>([]);
  const [ordersSessionFilter, setOrdersSessionFilter] = useState<string[]>([]);
  const [ordersStatusFilter, setOrdersStatusFilter] = useState<string[]>([]);
  const [ordersAmountFilterMode, setOrdersAmountFilterMode] = useState<AmountFilterMode>('all');
  const [ordersAmountExact, setOrdersAmountExact] = useState<number | string>('');
  const [ordersAmountMin, setOrdersAmountMin] = useState<number | string>('');
  const [ordersAmountMax, setOrdersAmountMax] = useState<number | string>('');
  const [ordersDateFrom, setOrdersDateFrom] = useState('');
  const [ordersDateTo, setOrdersDateTo] = useState('');
  const [ordersSortKey, setOrdersSortKey] = useState<AdminOrdersSortKey>('openedAt');
  const [ordersSortDirection, setOrdersSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedOrderIndex, setSelectedOrderIndex] = useState<number | null>(null);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState<AdminOrderMonitoringDetailView | null>(null);
  const [isOrderDetailLoading, setIsOrderDetailLoading] = useState(false);
  const [paymentlines, setPaymentlines] = useState<AdminPaymentlineView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState<{
    companies: string[];
    sessions: { value: string; label: string }[];
    orders: string[];
  }>({ companies: [], sessions: [], orders: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<number, UpdateAdminPaymentlinePayload>>({});
  const [savingLineId, setSavingLineId] = useState<number | null>(null);

  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [sessionFilter, setSessionFilter] = useState<string[]>([]);
  const [orderFilter, setOrderFilter] = useState<string[]>([]);
  const [methodFilter, setMethodFilter] = useState<string[]>([]);
  const [amountFilterMode, setAmountFilterMode] = useState<AmountFilterMode>('all');
  const [amountExact, setAmountExact] = useState<number | string>('');
  const [amountMin, setAmountMin] = useState<number | string>('');
  const [amountMax, setAmountMax] = useState<number | string>('');
  const [dateFilterMode, setDateFilterMode] = useState<DateFilterMode>('all');
  const [exactDate, setExactDate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [auditTarget, setAuditTarget] = useState<AdminPaymentlineView | null>(null);
  const [auditRows, setAuditRows] = useState<PaymentlineAuditView[]>([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);

  const [movementRows, setMovementRows] = useState<AdminTransferAuditView[]>([]);
  const [movementTotal, setMovementTotal] = useState(0);
  const [movementPage, setMovementPage] = useState(1);
  const [movementTab, setMovementTab] = useState<'transfers' | 'merges'>('transfers');
  const [isMovementLoading, setIsMovementLoading] = useState(false);
  const [hasLoadedMovements, setHasLoadedMovements] = useState(false);
  const [selectedMovementIndex, setSelectedMovementIndex] = useState<number | null>(null);
  const [movementFilters, setMovementFilters] = useState({
    search: '',
    sessionName: '',
    performedByName: '',
    sourceTableName: '',
    targetTableName: '',
    transferKind: '',
    dateFrom: '',
    dateTo: '',
  });

  const [mergeRows, setMergeRows] = useState<AdminMergeAuditView[]>([]);
  const [mergeTotal, setMergeTotal] = useState(0);
  const [mergePage, setMergePage] = useState(1);
  const [isMergeLoading, setIsMergeLoading] = useState(false);
  const [hasLoadedMerges, setHasLoadedMerges] = useState(false);
  const [selectedMergeIndex, setSelectedMergeIndex] = useState<number | null>(null);
  const [mergeFilters, setMergeFilters] = useState({
    search: '',
    sessionName: '',
    performedByName: '',
    tableName: '',
    kind: '',
    dateFrom: '',
    dateTo: '',
  });

  const [kitchenPrintRows, setKitchenPrintRows] = useState<AdminKitchenPrintLogView[]>([]);
  const [kitchenPrintTotal, setKitchenPrintTotal] = useState(0);
  const [kitchenPrintPage, setKitchenPrintPage] = useState(1);
  const [kitchenFilterOptions, setKitchenFilterOptions] = useState<{
    companies: string[];
    sessions: { value: string; label: string }[];
    orders: string[];
  }>({ companies: [], sessions: [], orders: [] });
  const [isKitchenPrintLoading, setIsKitchenPrintLoading] = useState(false);
  const [hasLoadedKitchenPrints, setHasLoadedKitchenPrints] = useState(false);

  const [kitchenCompanyFilter, setKitchenCompanyFilter] = useState<string[]>([]);
  const [kitchenSessionFilter, setKitchenSessionFilter] = useState<string[]>([]);
  const [kitchenOrderFilter, setKitchenOrderFilter] = useState<string[]>([]);

  const [sessionRows, setSessionRows] = useState<AdminSessionAuditView[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionFilterOptions, setSessionFilterOptions] = useState<{ sessions: { value: string; label: string }[] }>({ sessions: [] });
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [hasLoadedSessions, setHasLoadedSessions] = useState(false);

  const [sessionSessionFilter, setSessionSessionFilter] = useState<string[]>([]);
  const [sessionStatusFilter, setSessionStatusFilter] = useState<string[]>([]);
  const [sessionDateFilterMode, setSessionDateFilterMode] = useState<DateFilterMode>('all');
  const [sessionExactDate, setSessionExactDate] = useState('');
  const [sessionDateFrom, setSessionDateFrom] = useState('');
  const [sessionDateTo, setSessionDateTo] = useState('');
  const [sessionSortKey, setSessionSortKey] = useState<AdminSessionsSortKey>('openTime');
  const [sessionSortDirection, setSessionSortDirection] = useState<SortDirection>('desc');

  const [approvalRequestRows, setApprovalRequestRows] = useState<AdminApprovalRequestView[]>([]);
  const [approvalRequestTotal, setApprovalRequestTotal] = useState(0);
  const [approvalRequestPage, setApprovalRequestPage] = useState(1);
  const [approvalRequestFilterOptions, setApprovalRequestFilterOptions] = useState<{
    sessions: AdminApprovalRequestView['sessionId'][];
    actionTypes: AdminApprovalRequestView['actionType'][];
    reasons: AdminApprovalRequestView['reason'][];
    statuses: AdminApprovalRequestView['status'][];
  }>({ sessions: [], actionTypes: [], reasons: [], statuses: [] });
  const [isApprovalRequestLoading, setIsApprovalRequestLoading] = useState(false);
  const [hasLoadedApprovalRequests, setHasLoadedApprovalRequests] = useState(false);
  const [selectedApprovalRequestIndex, setSelectedApprovalRequestIndex] = useState<number | null>(null);
  const [selectedApprovalRequestDetail, setSelectedApprovalRequestDetail] = useState<AdminApprovalRequestDetailView | null>(null);
  const [isApprovalRequestDetailLoading, setIsApprovalRequestDetailLoading] = useState(false);

  const [approvalRequestSearch, setApprovalRequestSearch] = useState('');
  const [approvalRequestSearchFields, setApprovalRequestSearchFields] = useState<AdminApprovalRequestSearchField[]>([]);
  const [approvalRequestSessionFilter, setApprovalRequestSessionFilter] = useState<string[]>([]);
  const [approvalRequestActionTypeFilter, setApprovalRequestActionTypeFilter] = useState<string[]>([]);
  const [approvalRequestReasonFilter, setApprovalRequestReasonFilter] = useState<string[]>([]);
  const [approvalRequestStatusFilter, setApprovalRequestStatusFilter] = useState<string[]>([]);
  const [approvalRequestDateFrom, setApprovalRequestDateFrom] = useState('');
  const [approvalRequestDateTo, setApprovalRequestDateTo] = useState('');
  const [kdsAuditRows, setKdsAuditRows] = useState<AdminKdsAuditView[]>([]);
  const [kdsAuditTotal, setKdsAuditTotal] = useState(0);
  const [kdsAuditPage, setKdsAuditPage] = useState(1);
  const [kdsAuditFilterOptions, setKdsAuditFilterOptions] = useState<{ companies: string[]; sessions: { value: string; label: string }[] }>({ companies: [], sessions: [] });
  const [isKdsAuditLoading, setIsKdsAuditLoading] = useState(false);
  const [hasLoadedKdsAudit, setHasLoadedKdsAudit] = useState(false);

  const [kdsAuditCompanyFilter, setKdsAuditCompanyFilter] = useState<string[]>([]);
  const [kdsAuditKitchenStatusFilter, setKdsAuditKitchenStatusFilter] = useState<string[]>([]);
  const [kdsAuditOrderTypeFilter, setKdsAuditOrderTypeFilter] = useState<string[]>([]);
  const [kdsAuditOrderStatusFilter, setKdsAuditOrderStatusFilter] = useState<string[]>([]);
  const [kdsAuditSessionFilter, setKdsAuditSessionFilter] = useState<string>('');
  const [kdsAuditSearch, setKdsAuditSearch] = useState('');
  const [kdsAuditDateFilterMode, setKdsAuditDateFilterMode] = useState<DateFilterMode>('all');
  const [kdsAuditExactDate, setKdsAuditExactDate] = useState('');
  const [kdsAuditDateFrom, setKdsAuditDateFrom] = useState('');
  const [kdsAuditDateTo, setKdsAuditDateTo] = useState('');
  const [kdsAuditSortKey, setKdsAuditSortKey] = useState<AdminKdsAuditSortKey>('companyName');
  const [kdsAuditSortDirection, setKdsAuditSortDirection] = useState<SortDirection>('desc');
  const [kdsDrafts, setKdsDrafts] = useState<Record<string, UpdateAdminKdsStatusPayload>>({});
  const [savingKdsRowKey, setSavingKdsRowKey] = useState<string | null>(null);

  const [kdsHistoryTarget, setKdsHistoryTarget] = useState<AdminKdsAuditView | null>(null);
  const [kdsHistory, setKdsHistory] = useState<KdsAuditHistoryView | null>(null);
  const [isKdsHistoryLoading, setIsKdsHistoryLoading] = useState(false);

  const [errorRows, setErrorRows] = useState<LogEntryView[]>([]);
  const [errorTotal, setErrorTotal] = useState(0);
  const [errorPage, setErrorPage] = useState(1);
  const [errorFilterOptions, setErrorFilterOptions] = useState<{ tenants: { value: string; label: string }[] }>({ tenants: [] });
  const [isErrorLoading, setIsErrorLoading] = useState(false);
  const [hasLoadedErrors, setHasLoadedErrors] = useState(false);
  const [errorPlaceFilter, setErrorPlaceFilter] = useState<string>('');
  const [errorLevelFilter, setErrorLevelFilter] = useState<string>('');
  const [errorTenantFilter, setErrorTenantFilter] = useState<string>('');
  const [errorSearch, setErrorSearch] = useState('');
  const [errorDateFilterMode, setErrorDateFilterMode] = useState<DateFilterMode>('all');
  const [errorExactDate, setErrorExactDate] = useState('');
  const [errorDateFrom, setErrorDateFrom] = useState('');
  const [errorDateTo, setErrorDateTo] = useState('');
  const [errorStatusClassFilter, setErrorStatusClassFilter] = useState<string>('');
  const [errorIgnoredFilter, setErrorIgnoredFilter] = useState<'hide' | 'only' | 'all'>('hide');
  const [errorRefreshTick, setErrorRefreshTick] = useState(0);
  const [isSavingIgnore, setIsSavingIgnore] = useState(false);
  const savingIgnoreRef = useRef(false);
  const [selectedError, setSelectedError] = useState<LogEntryView | null>(null);

  const [ontopoRows, setOntopoRows] = useState<OntopoCallLogView[]>([]);
  const [ontopoTotal, setOntopoTotal] = useState(0);
  const [ontopoPage, setOntopoPage] = useState(1);
  const [ontopoFilterOptions, setOntopoFilterOptions] = useState<{ tenants: { value: string; label: string }[] }>({ tenants: [] });
  const [isOntopoLoading, setIsOntopoLoading] = useState(false);
  const [hasLoadedOntopo, setHasLoadedOntopo] = useState(false);
  const [ontopoTenantFilter, setOntopoTenantFilter] = useState<string>('');
  const [ontopoMethodFilter, setOntopoMethodFilter] = useState<string>('');
  const [ontopoStatusFilter, setOntopoStatusFilter] = useState<string>('');
  const [ontopoSearch, setOntopoSearch] = useState('');
  const [ontopoDateFilterMode, setOntopoDateFilterMode] = useState<DateFilterMode>('all');
  const [ontopoExactDate, setOntopoExactDate] = useState('');
  const [ontopoDateFrom, setOntopoDateFrom] = useState('');
  const [ontopoDateTo, setOntopoDateTo] = useState('');
  const [selectedOntopoLog, setSelectedOntopoLog] = useState<OntopoCallLogView | null>(null);

  const [woltRows, setWoltRows] = useState<WoltCallLogView[]>([]);
  const [woltTotal, setWoltTotal] = useState(0);
  const [woltPage, setWoltPage] = useState(1);
  const [woltFilterOptions, setWoltFilterOptions] = useState<{ tenants: { value: string; label: string }[] }>({ tenants: [] });
  const [isWoltLoading, setIsWoltLoading] = useState(false);
  const [hasLoadedWolt, setHasLoadedWolt] = useState(false);
  const [woltTenantFilter, setWoltTenantFilter] = useState('');
  const [woltDirectionFilter, setWoltDirectionFilter] = useState('');
  const [woltKindFilter, setWoltKindFilter] = useState('');
  const [woltStatusFilter, setWoltStatusFilter] = useState('');
  const [woltSearch, setWoltSearch] = useState('');
  const [woltDateFilterMode, setWoltDateFilterMode] = useState<DateFilterMode>('all');
  const [woltExactDate, setWoltExactDate] = useState('');
  const [woltDateFrom, setWoltDateFrom] = useState('');
  const [woltDateTo, setWoltDateTo] = useState('');
  const [selectedWoltLog, setSelectedWoltLog] = useState<WoltCallLogView | null>(null);
  const [tenbisRows, setTenbisRows] = useState<TenbisCallLogView[]>([]);
  const [tenbisTotal, setTenbisTotal] = useState(0);
  const [tenbisPage, setTenbisPage] = useState(1);
  const [tenbisFilterOptions, setTenbisFilterOptions] = useState<{ tenants: { value: string; label: string }[] }>({ tenants: [] });
  const [isTenbisLoading, setIsTenbisLoading] = useState(false);
  const [hasLoadedTenbis, setHasLoadedTenbis] = useState(false);
  const [tenbisTenantFilter, setTenbisTenantFilter] = useState<string>('');
  const [tenbisDirectionFilter, setTenbisDirectionFilter] = useState<string>('');
  const [tenbisKindFilter, setTenbisKindFilter] = useState<string>('');
  const [tenbisStatusFilter, setTenbisStatusFilter] = useState<string>('');
  const [tenbisSearch, setTenbisSearch] = useState('');
  const [tenbisDateFilterMode, setTenbisDateFilterMode] = useState<DateFilterMode>('all');
  const [tenbisExactDate, setTenbisExactDate] = useState('');
  const [tenbisDateFrom, setTenbisDateFrom] = useState('');
  const [tenbisDateTo, setTenbisDateTo] = useState('');
  const [selectedTenbisLog, setSelectedTenbisLog] = useState<TenbisCallLogView | null>(null);
  const [cibusRows, setCibusRows] = useState<CibusCallLogView[]>([]);
  const [cibusTotal, setCibusTotal] = useState(0);
  const [cibusPage, setCibusPage] = useState(1);
  const [cibusFilterOptions, setCibusFilterOptions] = useState<{ tenants: { value: string; label: string }[] }>({ tenants: [] });
  const [isCibusLoading, setIsCibusLoading] = useState(false);
  const [hasLoadedCibus, setHasLoadedCibus] = useState(false);
  const [cibusTenantFilter, setCibusTenantFilter] = useState<string>('');
  const [cibusDirectionFilter, setCibusDirectionFilter] = useState<string>('');
  const [cibusKindFilter, setCibusKindFilter] = useState<string>('');
  const [cibusStatusFilter, setCibusStatusFilter] = useState<string>('');
  const [cibusSearch, setCibusSearch] = useState('');
  const [cibusDateFilterMode, setCibusDateFilterMode] = useState<DateFilterMode>('all');
  const [cibusExactDate, setCibusExactDate] = useState('');
  const [cibusDateFrom, setCibusDateFrom] = useState('');
  const [cibusDateTo, setCibusDateTo] = useState('');
  const [selectedCibusLog, setSelectedCibusLog] = useState<CibusCallLogView | null>(null);

  // Reset every drill-in detail selection when switching tabs so each section returns to its list.
  useEffect(() => {
    setSelectedOrderIndex(null);
    setSelectedOrderDetail(null);
    setSelectedMovementIndex(null);
    setSelectedMergeIndex(null);
    setSelectedApprovalRequestIndex(null);
    setSelectedApprovalRequestDetail(null);
  }, [activeSection]);

  const ordersQuery = useMemo<AdminOrdersQuery>(() => ({
    page: ordersPage,
    pageSize: PAGE_SIZE,
    search: ordersSearch.trim() || undefined,
    company: ordersCompanyFilter.length ? ordersCompanyFilter : undefined,
    session: ordersSessionFilter.length ? ordersSessionFilter : undefined,
    status: ordersStatusFilter.length ? ordersStatusFilter : undefined,
    amountMode: ordersAmountFilterMode,
    amountExact: typeof ordersAmountExact === 'number' ? ordersAmountExact : undefined,
    amountMin: typeof ordersAmountMin === 'number' ? ordersAmountMin : undefined,
    amountMax: typeof ordersAmountMax === 'number' ? ordersAmountMax : undefined,
    dateFrom: ordersDateFrom || undefined,
    dateTo: ordersDateTo || undefined,
    sortKey: ordersSortKey,
    sortDirection: ordersSortDirection,
  }), [
    ordersPage,
    ordersSearch,
    ordersCompanyFilter,
    ordersSessionFilter,
    ordersStatusFilter,
    ordersAmountFilterMode,
    ordersAmountExact,
    ordersAmountMin,
    ordersAmountMax,
    ordersDateFrom,
    ordersDateTo,
    ordersSortKey,
    ordersSortDirection,
  ]);

  const ordersFiltersKey = useMemo(() => JSON.stringify({ ...ordersQuery, page: undefined }), [ordersQuery]);

  useEffect(() => {
    setOrdersPage(1);
  }, [ordersFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'orders') {
      return;
    }

    let active = true;
    setIsOrdersLoading(true);
    setError(null);

    adminOrdersService
      .list(ordersQuery)
      .then((result) => {
        if (!active) return;
        setOrdersRows(result.rows);
        setOrdersTotal(result.total);
        setOrdersFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load orders monitoring');
        }
      })
      .finally(() => {
        if (active) {
          setIsOrdersLoading(false);
          setHasLoadedOrders(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, ordersQuery]);

  const loadOrderDetail = useCallback(async (index: number) => {
    const row = ordersRows[index];
    if (!row) {
      return;
    }

    setSelectedOrderIndex(index);
    setSelectedOrderDetail(null);
    setIsOrderDetailLoading(true);
    setError(null);

    try {
      const detail = await adminOrdersService.getDetail(row.id);
      setSelectedOrderDetail(detail);
    } catch (detailError: unknown) {
      setError(detailError instanceof Error ? detailError.message : 'Failed to load order details');
      setSelectedOrderIndex(null);
    } finally {
      setIsOrderDetailLoading(false);
    }
  }, [ordersRows]);

  const openOrderDetail = useCallback((index: number) => {
    void loadOrderDetail(index);
  }, [loadOrderDetail]);

  const closeOrderDetail = useCallback(() => {
    setSelectedOrderIndex(null);
    setSelectedOrderDetail(null);
  }, []);

  const prevOrderDetail = useCallback(() => {
    if (selectedOrderIndex === null || selectedOrderIndex <= 0) {
      return;
    }
    void loadOrderDetail(selectedOrderIndex - 1);
  }, [loadOrderDetail, selectedOrderIndex]);

  const nextOrderDetail = useCallback(() => {
    if (selectedOrderIndex === null || selectedOrderIndex >= ordersRows.length - 1) {
      return;
    }
    void loadOrderDetail(selectedOrderIndex + 1);
  }, [loadOrderDetail, ordersRows.length, selectedOrderIndex]);

  const query = useMemo<AdminPaymentlinesQuery>(() => ({
    page,
    pageSize: PAGE_SIZE,
    status: statusFilter.length ? statusFilter : undefined,
    company: companyFilter.length ? companyFilter : undefined,
    session: sessionFilter.length ? sessionFilter : undefined,
    order: orderFilter.length ? orderFilter : undefined,
    method: methodFilter.length ? methodFilter : undefined,
    amountMode: amountFilterMode,
    amountExact: typeof amountExact === 'number' ? amountExact : undefined,
    amountMin: typeof amountMin === 'number' ? amountMin : undefined,
    amountMax: typeof amountMax === 'number' ? amountMax : undefined,
    dateMode: dateFilterMode,
    exactDate: exactDate || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    sortKey: sortKey === 'canceledAt' ? 'updatedAt' : sortKey,
    sortDirection,
  }), [
    page,
    statusFilter,
    companyFilter,
    sessionFilter,
    orderFilter,
    methodFilter,
    amountFilterMode,
    amountExact,
    amountMin,
    amountMax,
    dateFilterMode,
    exactDate,
    dateFrom,
    dateTo,
    sortKey,
    sortDirection,
  ]);

  const movementFiltersKey = useMemo(() => JSON.stringify(movementFilters), [
    movementFilters.search, movementFilters.sessionName, movementFilters.performedByName,
    movementFilters.sourceTableName, movementFilters.targetTableName, movementFilters.transferKind,
    movementFilters.dateFrom, movementFilters.dateTo,
  ]);

  const mergeFiltersKey = useMemo(() => JSON.stringify(mergeFilters), [
    mergeFilters.search, mergeFilters.sessionName, mergeFilters.performedByName,
    mergeFilters.tableName, mergeFilters.kind, mergeFilters.dateFrom, mergeFilters.dateTo,
  ]);

  // Reset to page 1 whenever a filter/sort changes.
  const filtersKey = useMemo(() => JSON.stringify({ ...query, page: undefined }), [query]);
  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError(null);
    adminPaymentsService
      .list(query)
      .then((result) => {
        if (!active) return;
        setPaymentlines(result.rows);
        setTotal(result.total);
        setFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load payments');
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
          setHasLoaded(true);
        }
      });

    return () => {
      active = false;
    };
  }, [query]);

  useEffect(() => {
    if (activeSection !== 'mergeLogs' || movementTab !== 'transfers') {
      return;
    }

    let active = true;
    setIsMovementLoading(true);
    setError(null);

    adminTransferAuditService
      .list({
        page: movementPage,
        pageSize: PAGE_SIZE,
        ...(movementFilters.search && { search: movementFilters.search }),
        ...(movementFilters.sessionName && { sessionName: movementFilters.sessionName }),
        ...(movementFilters.performedByName && { performedByName: movementFilters.performedByName }),
        ...(movementFilters.sourceTableName && { sourceTableName: movementFilters.sourceTableName }),
        ...(movementFilters.targetTableName && { targetTableName: movementFilters.targetTableName }),
        ...(movementFilters.transferKind && { transferKind: movementFilters.transferKind }),
        ...(movementFilters.dateFrom && { dateFrom: movementFilters.dateFrom }),
        ...(movementFilters.dateTo && { dateTo: movementFilters.dateTo }),
      })
      .then((result) => {
        if (!active) return;
        setMovementRows(result.rows);
        setMovementTotal(result.total);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load transfer audit log');
        }
      })
      .finally(() => {
        if (active) {
          setIsMovementLoading(false);
          setHasLoadedMovements(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, movementPage, movementTab, movementFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'mergeLogs' || movementTab !== 'merges') {
      return;
    }

    let active = true;
    setIsMergeLoading(true);
    setError(null);

    adminMergeAuditService
      .list({
        page: mergePage,
        pageSize: PAGE_SIZE,
        ...(mergeFilters.search && { search: mergeFilters.search }),
        ...(mergeFilters.sessionName && { sessionName: mergeFilters.sessionName }),
        ...(mergeFilters.performedByName && { performedByName: mergeFilters.performedByName }),
        ...(mergeFilters.tableName && { tableName: mergeFilters.tableName }),
        ...(mergeFilters.kind && { kind: mergeFilters.kind }),
        ...(mergeFilters.dateFrom && { dateFrom: mergeFilters.dateFrom }),
        ...(mergeFilters.dateTo && { dateTo: mergeFilters.dateTo }),
      })
      .then((result) => {
        if (!active) return;
        setMergeRows(result.rows);
        setMergeTotal(result.total);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load merge audit log');
        }
      })
      .finally(() => {
        if (active) {
          setIsMergeLoading(false);
          setHasLoadedMerges(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, movementTab, mergePage, mergeFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'kitchenPrints') {
      return;
    }

    let active = true;
    setIsKitchenPrintLoading(true);
    setError(null);

    adminKitchenPrintsService
      .list({
        page: kitchenPrintPage,
        pageSize: PAGE_SIZE,
        company: kitchenCompanyFilter.length ? kitchenCompanyFilter : undefined,
        session: kitchenSessionFilter.length ? kitchenSessionFilter : undefined,
        order: kitchenOrderFilter.length ? kitchenOrderFilter : undefined,
      })
      .then((result) => {
        if (!active) return;
        setKitchenPrintRows(result.rows);
        setKitchenPrintTotal(result.total);
        setKitchenFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load kitchen prints');
        }
      })
      .finally(() => {
        if (active) {
          setIsKitchenPrintLoading(false);
          setHasLoadedKitchenPrints(true);
        }
      });

    return () => {
      active = false;
    };
  }, [
    activeSection,
    kitchenPrintPage,
    kitchenCompanyFilter,
    kitchenSessionFilter,
    kitchenOrderFilter,
  ]);

  const kitchenFiltersKey = useMemo(
    () => JSON.stringify({
      company: kitchenCompanyFilter,
      session: kitchenSessionFilter,
      order: kitchenOrderFilter,
    }),
    [kitchenCompanyFilter, kitchenSessionFilter, kitchenOrderFilter],
  );

  useEffect(() => {
    setKitchenPrintPage(1);
  }, [kitchenFiltersKey]);

  const sessionQuery = useMemo<AdminSessionsQuery>(() => ({
    page: sessionPage,
    pageSize: PAGE_SIZE,
    session: sessionSessionFilter.length ? sessionSessionFilter : undefined,
    status: sessionStatusFilter.length ? sessionStatusFilter : undefined,
    dateMode: sessionDateFilterMode,
    exactDate: sessionExactDate || undefined,
    dateFrom: sessionDateFrom || undefined,
    dateTo: sessionDateTo || undefined,
    sortKey: sessionSortKey,
    sortDirection: sessionSortDirection,
  }), [
    sessionPage,
    sessionSessionFilter,
    sessionStatusFilter,
    sessionDateFilterMode,
    sessionExactDate,
    sessionDateFrom,
    sessionDateTo,
    sessionSortKey,
    sessionSortDirection,
  ]);

  const sessionFiltersKey = useMemo(() => JSON.stringify({ ...sessionQuery, page: undefined }), [sessionQuery]);
  useEffect(() => {
    setSessionPage(1);
  }, [sessionFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'sessions') {
      return;
    }

    let active = true;
    setIsSessionLoading(true);
    setError(null);

    adminSessionsService
      .list(sessionQuery)
      .then((result) => {
        if (!active) return;
        setSessionRows(result.rows);
        setSessionTotal(result.total);
        setSessionFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load sessions');
        }
      })
      .finally(() => {
        if (active) {
          setIsSessionLoading(false);
          setHasLoadedSessions(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, sessionQuery]);

  const approvalRequestQuery = useMemo<AdminApprovalRequestsQuery>(() => ({
    page: approvalRequestPage,
    pageSize: PAGE_SIZE,
    search: approvalRequestSearch.trim() || undefined,
    searchFields: approvalRequestSearchFields.length ? approvalRequestSearchFields : undefined,
    session: approvalRequestSessionFilter.length ? approvalRequestSessionFilter : undefined,
    actionType: approvalRequestActionTypeFilter.length ? approvalRequestActionTypeFilter : undefined,
    reason: approvalRequestReasonFilter.length ? approvalRequestReasonFilter : undefined,
    status: approvalRequestStatusFilter.length ? approvalRequestStatusFilter : undefined,
    dateFrom: approvalRequestDateFrom || undefined,
    dateTo: approvalRequestDateTo || undefined,
  }), [
    approvalRequestPage,
    approvalRequestSearch,
    approvalRequestSearchFields,
    approvalRequestSessionFilter,
    approvalRequestActionTypeFilter,
    approvalRequestReasonFilter,
    approvalRequestStatusFilter,
    approvalRequestDateFrom,
    approvalRequestDateTo,
  ]);

  const approvalRequestFiltersKey = useMemo(() => JSON.stringify({
    ...approvalRequestQuery,
    page: undefined,
  }), [approvalRequestQuery]);

  useEffect(() => {
    setApprovalRequestPage(1);
  }, [approvalRequestFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'approvalRequests') {
      return;
    }

    let active = true;
    setIsApprovalRequestLoading(true);
    setError(null);

    adminApprovalRequestsService
      .list(approvalRequestQuery)
      .then((result) => {
        if (!active) return;
        setApprovalRequestRows(result.rows);
        setApprovalRequestTotal(result.total);
        setApprovalRequestFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'טעינת בקשות האישור נכשלה');
        }
      })
      .finally(() => {
        if (active) {
          setIsApprovalRequestLoading(false);
          setHasLoadedApprovalRequests(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, approvalRequestQuery, approvalRequestsRevision]);

  const kdsAuditQuery = useMemo<AdminKdsAuditQuery>(() => ({
    page: kdsAuditPage,
    pageSize: PAGE_SIZE,
    company: kdsAuditCompanyFilter.length ? kdsAuditCompanyFilter : undefined,
    kitchenStatus: kdsAuditKitchenStatusFilter.length ? kdsAuditKitchenStatusFilter : undefined,
    orderType: kdsAuditOrderTypeFilter.length ? kdsAuditOrderTypeFilter : undefined,
    orderStatus: kdsAuditOrderStatusFilter.length ? kdsAuditOrderStatusFilter : undefined,
    sessionPk: kdsAuditSessionFilter ? Number(kdsAuditSessionFilter) : undefined,
    search: kdsAuditSearch.trim() || undefined,
    dateMode: kdsAuditDateFilterMode,
    exactDate: kdsAuditExactDate || undefined,
    dateFrom: kdsAuditDateFrom || undefined,
    dateTo: kdsAuditDateTo || undefined,
    sortKey: kdsAuditSortKey,
    sortDirection: kdsAuditSortDirection,
  }), [
    kdsAuditPage,
    kdsAuditCompanyFilter,
    kdsAuditKitchenStatusFilter,
    kdsAuditOrderTypeFilter,
    kdsAuditOrderStatusFilter,
    kdsAuditSessionFilter,
    kdsAuditSearch,
    kdsAuditDateFilterMode,
    kdsAuditExactDate,
    kdsAuditDateFrom,
    kdsAuditDateTo,
    kdsAuditSortKey,
    kdsAuditSortDirection,
  ]);

  const kdsAuditFiltersKey = useMemo(() => JSON.stringify({ ...kdsAuditQuery, page: undefined }), [kdsAuditQuery]);

  useEffect(() => {
    setKdsAuditPage(1);
  }, [kdsAuditFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'kdsAudit') {
      return;
    }

    let active = true;
    setIsKdsAuditLoading(true);
    setError(null);

    adminKdsAuditService
      .list(kdsAuditQuery)
      .then((result) => {
        if (!active) return;
        setKdsAuditRows(result.rows);
        setKdsAuditTotal(result.total);
        setKdsAuditFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'טעינת יומן המטבח נכשלה');
        }
      })
      .finally(() => {
        if (active) {
          setIsKdsAuditLoading(false);
          setHasLoadedKdsAudit(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, kdsAuditQuery]);

  const errorQuery = useMemo<LogsQuery>(() => ({
    page: errorPage,
    pageSize: PAGE_SIZE,
    place: (errorPlaceFilter || undefined) as LogsQuery['place'],
    level: (errorLevelFilter || undefined) as LogsQuery['level'],
    tenantId: errorTenantFilter || undefined,
    search: errorSearch.trim() || undefined,
    dateMode: errorDateFilterMode,
    exactDate: errorExactDate || undefined,
    dateFrom: errorDateFrom || undefined,
    dateTo: errorDateTo || undefined,
    statusClass: (errorStatusClassFilter || undefined) as LogsQuery['statusClass'],
    ignoredFilter: errorIgnoredFilter,
  }), [
    errorPage,
    errorPlaceFilter,
    errorLevelFilter,
    errorTenantFilter,
    errorSearch,
    errorDateFilterMode,
    errorExactDate,
    errorDateFrom,
    errorDateTo,
    errorStatusClassFilter,
    errorIgnoredFilter,
  ]);

  const errorFiltersKey = useMemo(() => JSON.stringify({ ...errorQuery, page: undefined }), [errorQuery]);
  useEffect(() => {
    setErrorPage(1);
  }, [errorFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'errors') {
      return;
    }

    let active = true;
    setIsErrorLoading(true);
    setError(null);

    adminLogsService
      .list(errorQuery)
      .then((result) => {
        if (!active) return;
        setErrorRows(result.rows);
        setErrorTotal(result.total);
        setErrorFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'טעינת יומן השגיאות נכשלה');
        }
      })
      .finally(() => {
        if (active) {
          setIsErrorLoading(false);
          setHasLoadedErrors(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, errorQuery, errorRefreshTick]);

  const ontopoQuery = useMemo<OntopoCallLogsQuery>(() => ({
    page: ontopoPage,
    pageSize: PAGE_SIZE,
    tenantId: ontopoTenantFilter || undefined,
    method: (ontopoMethodFilter || undefined) as OntopoCallLogsQuery['method'],
    status: (ontopoStatusFilter || undefined) as OntopoCallLogsQuery['status'],
    search: ontopoSearch.trim() || undefined,
    dateMode: ontopoDateFilterMode,
    exactDate: ontopoExactDate || undefined,
    dateFrom: ontopoDateFrom || undefined,
    dateTo: ontopoDateTo || undefined,
  }), [
    ontopoPage,
    ontopoTenantFilter,
    ontopoMethodFilter,
    ontopoStatusFilter,
    ontopoSearch,
    ontopoDateFilterMode,
    ontopoExactDate,
    ontopoDateFrom,
    ontopoDateTo,
  ]);

  const ontopoFiltersKey = useMemo(() => JSON.stringify({ ...ontopoQuery, page: undefined }), [ontopoQuery]);
  useEffect(() => {
    setOntopoPage(1);
  }, [ontopoFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'ontopo') {
      return;
    }

    let active = true;
    setIsOntopoLoading(true);
    setError(null);

    adminOntopoService
      .list(ontopoQuery)
      .then((result) => {
        if (!active) return;
        setOntopoRows(result.rows);
        setOntopoTotal(result.total);
        setOntopoFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'טעינת מעקב אונטופו נכשלה');
        }
      })
      .finally(() => {
        if (active) {
          setIsOntopoLoading(false);
          setHasLoadedOntopo(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, ontopoQuery]);

  const clearOntopoFilters = () => {
    setOntopoTenantFilter('');
    setOntopoMethodFilter('');
    setOntopoStatusFilter('');
    setOntopoSearch('');
    setOntopoDateFilterMode('all');
    setOntopoExactDate('');
    setOntopoDateFrom('');
    setOntopoDateTo('');
  };

  const openOntopoDetail = (row: OntopoCallLogView) => setSelectedOntopoLog(row);
  const closeOntopoDetail = () => setSelectedOntopoLog(null);

  const woltQuery = useMemo<WoltCallLogsQuery>(() => ({
    page: woltPage,
    pageSize: PAGE_SIZE,
    tenantId: woltTenantFilter || undefined,
    direction: (woltDirectionFilter || undefined) as WoltCallLogsQuery['direction'],
    kind: (woltKindFilter || undefined) as WoltCallLogsQuery['kind'],
    status: (woltStatusFilter || undefined) as WoltCallLogsQuery['status'],
    search: woltSearch.trim() || undefined,
    dateMode: woltDateFilterMode,
    exactDate: woltExactDate || undefined,
    dateFrom: woltDateFrom || undefined,
    dateTo: woltDateTo || undefined,
  }), [
    woltPage,
    woltTenantFilter,
    woltDirectionFilter,
    woltKindFilter,
    woltStatusFilter,
    woltSearch,
    woltDateFilterMode,
    woltExactDate,
    woltDateFrom,
    woltDateTo,
  ]);

  const woltFiltersKey = useMemo(() => JSON.stringify({ ...woltQuery, page: undefined }), [woltQuery]);
  useEffect(() => {
    setWoltPage(1);
  }, [woltFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'wolt') {
      return;
    }
    let active = true;
    setIsWoltLoading(true);
    setError(null);
    adminWoltService.list(woltQuery)
      .then((result) => {
        if (!active) return;
        setWoltRows(result.rows);
        setWoltTotal(result.total);
        setWoltFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'טעינת מעקב וולט נכשלה');
      })
      .finally(() => {
        if (active) {
          setIsWoltLoading(false);
          setHasLoadedWolt(true);
        }
      });
    return () => { active = false; };
  }, [activeSection, woltQuery]);

  const clearWoltFilters = () => {
    setWoltTenantFilter('');
    setWoltDirectionFilter('');
    setWoltKindFilter('');
    setWoltStatusFilter('');
    setWoltSearch('');
    setWoltDateFilterMode('all');
    setWoltExactDate('');
    setWoltDateFrom('');
    setWoltDateTo('');
  };
  const openWoltDetail = (row: WoltCallLogView) => setSelectedWoltLog(row);
  const closeWoltDetail = () => setSelectedWoltLog(null);

  const tenbisQuery = useMemo<TenbisCallLogsQuery>(() => ({
    page: tenbisPage,
    pageSize: PAGE_SIZE,
    tenantId: tenbisTenantFilter || undefined,
    direction: (tenbisDirectionFilter || undefined) as TenbisCallLogsQuery['direction'],
    kind: tenbisKindFilter || undefined,
    status: (tenbisStatusFilter || undefined) as TenbisCallLogsQuery['status'],
    search: tenbisSearch.trim() || undefined,
    dateMode: tenbisDateFilterMode,
    exactDate: tenbisExactDate || undefined,
    dateFrom: tenbisDateFrom || undefined,
    dateTo: tenbisDateTo || undefined,
  }), [
    tenbisPage,
    tenbisTenantFilter,
    tenbisDirectionFilter,
    tenbisKindFilter,
    tenbisStatusFilter,
    tenbisSearch,
    tenbisDateFilterMode,
    tenbisExactDate,
    tenbisDateFrom,
    tenbisDateTo,
  ]);

  const tenbisFiltersKey = useMemo(() => JSON.stringify({ ...tenbisQuery, page: undefined }), [tenbisQuery]);
  useEffect(() => {
    setTenbisPage(1);
  }, [tenbisFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'tenbis') {
      return;
    }

    let active = true;
    setIsTenbisLoading(true);
    setError(null);

    adminTenbisService
      .list(tenbisQuery)
      .then((result) => {
        if (!active) return;
        setTenbisRows(result.rows);
        setTenbisTotal(result.total);
        setTenbisFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'טעינת מעקב תן ביס נכשלה');
        }
      })
      .finally(() => {
        if (active) {
          setIsTenbisLoading(false);
          setHasLoadedTenbis(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, tenbisQuery]);

  const clearTenbisFilters = () => {
    setTenbisTenantFilter('');
    setTenbisDirectionFilter('');
    setTenbisKindFilter('');
    setTenbisStatusFilter('');
    setTenbisSearch('');
    setTenbisDateFilterMode('all');
    setTenbisExactDate('');
    setTenbisDateFrom('');
    setTenbisDateTo('');
  };

  const openTenbisDetail = (row: TenbisCallLogView) => setSelectedTenbisLog(row);
  const closeTenbisDetail = () => setSelectedTenbisLog(null);

  const cibusQuery = useMemo<CibusCallLogsQuery>(() => ({
    page: cibusPage,
    pageSize: PAGE_SIZE,
    tenantId: cibusTenantFilter || undefined,
    direction: (cibusDirectionFilter || undefined) as CibusCallLogsQuery['direction'],
    kind: cibusKindFilter || undefined,
    status: (cibusStatusFilter || undefined) as CibusCallLogsQuery['status'],
    search: cibusSearch.trim() || undefined,
    dateMode: cibusDateFilterMode,
    exactDate: cibusExactDate || undefined,
    dateFrom: cibusDateFrom || undefined,
    dateTo: cibusDateTo || undefined,
  }), [
    cibusPage,
    cibusTenantFilter,
    cibusDirectionFilter,
    cibusKindFilter,
    cibusStatusFilter,
    cibusSearch,
    cibusDateFilterMode,
    cibusExactDate,
    cibusDateFrom,
    cibusDateTo,
  ]);

  const cibusFiltersKey = useMemo(() => JSON.stringify({ ...cibusQuery, page: undefined }), [cibusQuery]);
  useEffect(() => {
    setCibusPage(1);
  }, [cibusFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'cibus') {
      return;
    }

    let active = true;
    setIsCibusLoading(true);
    setError(null);

    adminCibusService
      .list(cibusQuery)
      .then((result) => {
        if (!active) return;
        setCibusRows(result.rows);
        setCibusTotal(result.total);
        setCibusFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'טעינת מעקב סיבוס נכשלה');
        }
      })
      .finally(() => {
        if (active) {
          setIsCibusLoading(false);
          setHasLoadedCibus(true);
        }
      });

    return () => {
      active = false;
    };
  }, [activeSection, cibusQuery]);

  const clearCibusFilters = () => {
    setCibusTenantFilter('');
    setCibusDirectionFilter('');
    setCibusKindFilter('');
    setCibusStatusFilter('');
    setCibusSearch('');
    setCibusDateFilterMode('all');
    setCibusExactDate('');
    setCibusDateFrom('');
    setCibusDateTo('');
  };

  const openCibusDetail = (row: CibusCallLogView) => setSelectedCibusLog(row);
  const closeCibusDetail = () => setSelectedCibusLog(null);

  const clearErrorFilters = () => {
    setErrorPlaceFilter('');
    setErrorLevelFilter('');
    setErrorTenantFilter('');
    setErrorSearch('');
    setErrorDateFilterMode('all');
    setErrorExactDate('');
    setErrorDateFrom('');
    setErrorDateTo('');
    setErrorStatusClassFilter('');
    setErrorIgnoredFilter('hide');
  };

  const openErrorDetail = (row: LogEntryView) => setSelectedError(row);
  const closeErrorDetail = () => setSelectedError(null);

  // --- מעקב רסטיגו (super-admin outbound-BI monitoring) ---
  const [restigoRows, setRestigoRows] = useState<RestigoCallLogView[]>([]);
  const [restigoTotal, setRestigoTotal] = useState(0);
  const [restigoPage, setRestigoPage] = useState(1);
  const [restigoFilterOptions, setRestigoFilterOptions] = useState<{ tenants: { value: string; label: string }[] }>({ tenants: [] });
  const [isRestigoLoading, setIsRestigoLoading] = useState(false);
  const [hasLoadedRestigo, setHasLoadedRestigo] = useState(false);
  const [restigoTenantFilter, setRestigoTenantFilter] = useState('');
  const [restigoKindFilter, setRestigoKindFilter] = useState('');
  const [restigoStatusFilter, setRestigoStatusFilter] = useState('');
  const [restigoSearch, setRestigoSearch] = useState('');
  const [restigoDateFilterMode, setRestigoDateFilterMode] = useState<DateFilterMode>('all');
  const [restigoExactDate, setRestigoExactDate] = useState('');
  const [restigoDateFrom, setRestigoDateFrom] = useState('');
  const [restigoDateTo, setRestigoDateTo] = useState('');
  const [restigoRefreshTick, setRestigoRefreshTick] = useState(0);
  const [selectedRestigo, setSelectedRestigo] = useState<RestigoCallLogView | null>(null);
  const restigoResendLockRef = useRef(false);
  const [isResendingRestigo, setIsResendingRestigo] = useState(false);

  const restigoQuery = useMemo<RestigoCallLogsQuery>(() => ({
    page: restigoPage,
    pageSize: PAGE_SIZE,
    tenantId: restigoTenantFilter || undefined,
    kind: (restigoKindFilter || undefined) as RestigoCallLogsQuery['kind'],
    status: (restigoStatusFilter || undefined) as RestigoCallLogsQuery['status'],
    search: restigoSearch.trim() || undefined,
    dateMode: restigoDateFilterMode,
    exactDate: restigoExactDate || undefined,
    dateFrom: restigoDateFrom || undefined,
    dateTo: restigoDateTo || undefined,
  }), [
    restigoPage,
    restigoTenantFilter,
    restigoKindFilter,
    restigoStatusFilter,
    restigoSearch,
    restigoDateFilterMode,
    restigoExactDate,
    restigoDateFrom,
    restigoDateTo,
  ]);

  const restigoFiltersKey = useMemo(() => JSON.stringify({ ...restigoQuery, page: undefined }), [restigoQuery]);
  useEffect(() => {
    setRestigoPage(1);
  }, [restigoFiltersKey]);

  useEffect(() => {
    if (activeSection !== 'restigo') {
      return;
    }
    let active = true;
    setIsRestigoLoading(true);
    setError(null);
    adminRestigoService
      .list(restigoQuery)
      .then((result) => {
        if (!active) return;
        setRestigoRows(result.rows);
        setRestigoTotal(result.total);
        setRestigoFilterOptions(result.filterOptions);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'טעינת מעקב רסטיגו נכשלה');
        }
      })
      .finally(() => {
        if (active) {
          setIsRestigoLoading(false);
          setHasLoadedRestigo(true);
        }
      });
    return () => {
      active = false;
    };
  }, [activeSection, restigoQuery, restigoRefreshTick]);

  const clearRestigoFilters = () => {
    setRestigoTenantFilter('');
    setRestigoKindFilter('');
    setRestigoStatusFilter('');
    setRestigoSearch('');
    setRestigoDateFilterMode('all');
    setRestigoExactDate('');
    setRestigoDateFrom('');
    setRestigoDateTo('');
  };

  const openRestigoDetail = (row: RestigoCallLogView) => setSelectedRestigo(row);
  const closeRestigoDetail = () => setSelectedRestigo(null);
  const restigoTotalPages = Math.max(1, Math.ceil(restigoTotal / PAGE_SIZE));
  const restigoTenantOptions = useMemo(() => restigoFilterOptions.tenants, [restigoFilterOptions]);

  // Manual re-push for a failed row's session; re-entry guarded, refetches the page on success.
  const resendRestigo = async (row: RestigoCallLogView) => {
    if (restigoResendLockRef.current || row.sessionId == null || !row.tenantId) {
      return;
    }
    restigoResendLockRef.current = true;
    setIsResendingRestigo(true);
    setError(null);
    try {
      await adminRestigoService.resend(row.tenantId, row.sessionId);
      setSelectedRestigo(null);
      setRestigoRefreshTick((tick) => tick + 1);
    } catch (mutateError: unknown) {
      setError(mutateError instanceof Error ? mutateError.message : 'שליחה חוזרת לרסטיגו נכשלה');
    } finally {
      restigoResendLockRef.current = false;
      setIsResendingRestigo(false);
    }
  };

  // Mute/un-mute a fingerprint via its row id; re-entry guarded, refetches the page on success.
  const ignoreSelectedError = async () => {
    if (savingIgnoreRef.current || !selectedError) {
      return;
    }
    savingIgnoreRef.current = true;
    setIsSavingIgnore(true);
    setError(null);
    try {
      await adminLogsService.ignore(selectedError.id);
      setSelectedError(null);
      setErrorRefreshTick((tick) => tick + 1);
    } catch (mutateError: unknown) {
      setError(mutateError instanceof Error ? mutateError.message : 'ההתעלמות מהשגיאה נכשלה');
    } finally {
      savingIgnoreRef.current = false;
      setIsSavingIgnore(false);
    }
  };

  const unignoreSelectedError = async () => {
    if (savingIgnoreRef.current || !selectedError) {
      return;
    }
    savingIgnoreRef.current = true;
    setIsSavingIgnore(true);
    setError(null);
    try {
      await adminLogsService.unignore(selectedError.id);
      setSelectedError(null);
      setErrorRefreshTick((tick) => tick + 1);
    } catch (mutateError: unknown) {
      setError(mutateError instanceof Error ? mutateError.message : 'ביטול ההתעלמות נכשל');
    } finally {
      savingIgnoreRef.current = false;
      setIsSavingIgnore(false);
    }
  };

  const loadApprovalRequestDetail = useCallback(async (index: number) => {
    const row = approvalRequestRows[index];
    if (!row) {
      return;
    }

    setSelectedApprovalRequestIndex(index);
    setSelectedApprovalRequestDetail(null);
    setIsApprovalRequestDetailLoading(true);
    setError(null);

    try {
      const detail = await adminApprovalRequestsService.getDetail(row.id);
      setSelectedApprovalRequestDetail(detail);
    } catch (detailError: unknown) {
      setError(detailError instanceof Error ? detailError.message : 'Failed to load approval request details');
      setSelectedApprovalRequestIndex(null);
    } finally {
      setIsApprovalRequestDetailLoading(false);
    }
  }, [approvalRequestRows]);

  const openApprovalRequestDetail = useCallback((index: number) => {
    void loadApprovalRequestDetail(index);
  }, [loadApprovalRequestDetail]);

  const closeApprovalRequestDetail = useCallback(() => {
    setSelectedApprovalRequestIndex(null);
    setSelectedApprovalRequestDetail(null);
  }, []);

  const prevApprovalRequestDetail = useCallback(() => {
    if (selectedApprovalRequestIndex === null || selectedApprovalRequestIndex <= 0) {
      return;
    }
    void loadApprovalRequestDetail(selectedApprovalRequestIndex - 1);
  }, [loadApprovalRequestDetail, selectedApprovalRequestIndex]);

  const nextApprovalRequestDetail = useCallback(() => {
    if (selectedApprovalRequestIndex === null || selectedApprovalRequestIndex >= approvalRequestRows.length - 1) {
      return;
    }
    void loadApprovalRequestDetail(selectedApprovalRequestIndex + 1);
  }, [approvalRequestRows.length, loadApprovalRequestDetail, selectedApprovalRequestIndex]);

  const sessionOptions = filterOptions.sessions;

  const ordersTotalPages = Math.max(1, Math.ceil(ordersTotal / PAGE_SIZE));
  const ordersCompanyOptions = useMemo(
    () => ordersFilterOptions.companies.map((companyName) => ({ value: companyName, label: companyName })),
    [ordersFilterOptions.companies],
  );
  const ordersSessionOptions = ordersFilterOptions.sessions;
  const ordersStatusOptions = useMemo(
    () => ordersFilterOptions.statuses.map((status) => ({ value: status, label: status === 'open' ? 'פתוחה' : 'סגורה' })),
    [ordersFilterOptions.statuses],
  );

  const companyOptions = useMemo(
    () => filterOptions.companies.map((companyName) => ({ value: companyName, label: companyName })),
    [filterOptions.companies],
  );

  const orderOptions = useMemo(
    () => filterOptions.orders.map((orderCode) => ({ value: orderCode, label: orderCode })),
    [filterOptions.orders],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const movementTotalPages = Math.max(1, Math.ceil(movementTotal / PAGE_SIZE));
  const kitchenPrintTotalPages = Math.max(1, Math.ceil(kitchenPrintTotal / PAGE_SIZE));
  const sessionTotalPages = Math.max(1, Math.ceil(sessionTotal / PAGE_SIZE));
  const approvalRequestTotalPages = Math.max(1, Math.ceil(approvalRequestTotal / PAGE_SIZE));
  const sessionSessionOptions = sessionFilterOptions.sessions;

  const kdsAuditTotalPages = Math.max(1, Math.ceil(kdsAuditTotal / PAGE_SIZE));
  const kdsAuditCompanyOptions = useMemo(
    () => kdsAuditFilterOptions.companies.map((companyName) => ({ value: companyName, label: companyName })),
    [kdsAuditFilterOptions.companies],
  );
  const kdsAuditSessionOptions = kdsAuditFilterOptions.sessions;

  const errorTotalPages = Math.max(1, Math.ceil(errorTotal / PAGE_SIZE));
  const errorTenantOptions = errorFilterOptions.tenants;

  const ontopoTotalPages = Math.max(1, Math.ceil(ontopoTotal / PAGE_SIZE));
  const ontopoTenantOptions = ontopoFilterOptions.tenants;
  const tenbisTotalPages = Math.max(1, Math.ceil(tenbisTotal / PAGE_SIZE));
  const tenbisTenantOptions = tenbisFilterOptions.tenants;
  const cibusTotalPages = Math.max(1, Math.ceil(cibusTotal / PAGE_SIZE));
  const cibusTenantOptions = cibusFilterOptions.tenants;

  const woltTotalPages = Math.max(1, Math.ceil(woltTotal / PAGE_SIZE));
  const woltTenantOptions = woltFilterOptions.tenants;

  const kitchenCompanyOptions = useMemo(
    () => kitchenFilterOptions.companies.map((companyName) => ({ value: companyName, label: companyName })),
    [kitchenFilterOptions.companies],
  );

  const kitchenSessionOptions = kitchenFilterOptions.sessions;

  const kitchenOrderOptions = useMemo(
    () => kitchenFilterOptions.orders.map((orderCode) => ({ value: orderCode, label: orderCode })),
    [kitchenFilterOptions.orders],
  );

  const toggleSort = (nextKey: SortKey) => {
    if (nextKey === sortKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(nextKey);
    setSortDirection(nextKey === 'amount' ? 'desc' : 'asc');
  };

  const toggleOrdersSort = (nextKey: AdminOrdersSortKey) => {
    if (nextKey === ordersSortKey) {
      setOrdersSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setOrdersSortKey(nextKey);
    setOrdersSortDirection(nextKey === 'openedAt' || nextKey === 'closedAt' || nextKey === 'totalAmount' ? 'desc' : 'asc');
  };

  const clearOrdersFilters = () => {
    setOrdersSearch('');
    setOrdersCompanyFilter([]);
    setOrdersSessionFilter([]);
    setOrdersStatusFilter([]);
    setOrdersAmountFilterMode('all');
    setOrdersAmountExact('');
    setOrdersAmountMin('');
    setOrdersAmountMax('');
    setOrdersDateFrom('');
    setOrdersDateTo('');
  };

  const clearFilters = () => {
    setStatusFilter([]);
    setCompanyFilter([]);
    setSessionFilter([]);
    setOrderFilter([]);
    setMethodFilter([]);
    setAmountFilterMode('all');
    setAmountExact('');
    setAmountMin('');
    setAmountMax('');
    setDateFilterMode('all');
    setExactDate('');
    setDateFrom('');
    setDateTo('');
  };

  const getDraftForLine = (line: AdminPaymentlineView): UpdateAdminPaymentlinePayload => (
    drafts[line.id] ?? {
      status: line.status,
      method: line.method,
      amount: line.amount,
    }
  );

  const updateDraft = (lineId: number, patch: Partial<UpdateAdminPaymentlinePayload>) => {
    setDrafts((current) => {
      const base = current[lineId] ?? paymentlines.find((line) => line.id === lineId);
      if (!base) {
        return current;
      }
      const baseDraft: UpdateAdminPaymentlinePayload = {
        status: base.status,
        method: base.method,
        amount: base.amount,
      };
      return {
        ...current,
        [lineId]: {
          ...baseDraft,
          ...(current[lineId] ?? {}),
          ...patch,
        },
      };
    });
  };

  const saveLine = async (line: AdminPaymentlineView, nextDraft?: UpdateAdminPaymentlinePayload) => {
    const draft = nextDraft ?? getDraftForLine(line);
    setSavingLineId(line.id);
    setError(null);
    try {
      const updated = await adminPaymentsService.update(line.id, draft);
      setPaymentlines((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      setDrafts((current) => {
        const next = { ...current };
        delete next[line.id];
        return next;
      });
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to update payment row');
    } finally {
      setSavingLineId(null);
    }
  };

  const applyAndSave = (line: AdminPaymentlineView, patch: Partial<UpdateAdminPaymentlinePayload>) => {
    const minAmount = Math.max(0, Number((line.globalTipCollected ?? 0).toFixed(1)));
    const requestedAmount = typeof patch.amount === 'number' ? patch.amount : undefined;
    const normalizedPatch = requestedAmount != null && requestedAmount < minAmount
      ? { ...patch, amount: minAmount }
      : patch;
    const nextDraft = { ...getDraftForLine(line), ...normalizedPatch };
    updateDraft(line.id, normalizedPatch);
    if (typeof nextDraft.amount !== 'number') {
      return;
    }
    void saveLine(line, nextDraft);
  };

  const getKdsDraftForLine = (line: AdminKdsAuditView): UpdateAdminKdsStatusPayload => (
    kdsDrafts[kdsRowKey(line)] ?? { status: line.kitchenStatus ?? null }
  );

  const updateKdsDraft = (row: AdminKdsAuditView, patch: Partial<UpdateAdminKdsStatusPayload>) => {
    const key = kdsRowKey(row);
    setKdsDrafts((current) => {
      const existingDraft = current[key];
      const existingRow = kdsAuditRows.find((candidate) => kdsRowsMatch(candidate, row));
      if (!existingDraft && !existingRow) {
        return current;
      }
      const baseDraft: UpdateAdminKdsStatusPayload = {
        status: existingDraft ? existingDraft.status : existingRow?.kitchenStatus ?? null,
      };
      return {
        ...current,
        [key]: {
          ...baseDraft,
          ...(current[key] ?? {}),
          ...patch,
        },
      };
    });
  };

  const saveKdsLine = async (line: AdminKdsAuditView, nextDraft?: UpdateAdminKdsStatusPayload) => {
    const draft = nextDraft ?? getKdsDraftForLine(line);
    const isSelection = line.selectionId != null;
    setSavingKdsRowKey(kdsRowKey(line));
    setError(null);
    try {
      const updated = isSelection
        ? await adminKdsAuditService.updateSelection(line.selectionId as number, draft)
        : await adminKdsAuditService.update(line.orderlineId, draft);
      let authoritativeCurrent = updated;
      try {
        const refreshedHistory = isSelection
          ? await adminKdsAuditService.selectionHistory(line.selectionId as number)
          : await adminKdsAuditService.history(line.orderlineId);
        authoritativeCurrent = refreshedHistory.current ?? updated;
        setKdsHistory((current) => {
          if (!current?.current || !kdsRowsMatch(current.current, updated)) {
            return current;
          }
          return refreshedHistory;
        });
      } catch {
        // Keep optimistic updated row if history refresh fails.
      }

      setKdsAuditRows((current) => current.map((row) => (kdsRowsMatch(row, updated) ? authoritativeCurrent : row)));
      setKdsDrafts((current) => {
        const next = { ...current };
        delete next[kdsRowKey(line)];
        return next;
      });
      setKdsHistoryTarget((current) => (current && kdsRowsMatch(current, updated) ? authoritativeCurrent : current));
      setKdsHistory((current) => {
        if (!current?.current || !kdsRowsMatch(current.current, updated)) {
          return current;
        }
        return {
          ...current,
          current: authoritativeCurrent,
        };
      });
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to update KDS status');
    } finally {
      setSavingKdsRowKey(null);
    }
  };

  const applyAndSaveKdsStatus = (line: AdminKdsAuditView, status: AdminKdsAuditView['kitchenStatus']) => {
    const nextDraft: UpdateAdminKdsStatusPayload = { status: status ?? null };
    updateKdsDraft(line, nextDraft);
    void saveKdsLine(line, nextDraft);
  };

  const openAuditModal = async (line: AdminPaymentlineView) => {
    setAuditTarget(line);
    setIsAuditLoading(true);
    setAuditRows([]);
    try {
      const rows = await adminPaymentsService.listAudit(line.id);
      setAuditRows(rows);
    } catch (auditError: unknown) {
      setError(auditError instanceof Error ? auditError.message : 'Failed to load audit history');
    } finally {
      setIsAuditLoading(false);
    }
  };

  const closeAuditModal = () => setAuditTarget(null);

  const selectMovementTab = (tab: 'transfers' | 'merges') => {
    setMovementTab(tab);
    setMovementPage(1);
    setSelectedMovementIndex(null);
    setSelectedMergeIndex(null);
  };

  const updateMovementFilters: typeof setMovementFilters = useCallback((value) => {
    setMovementFilters(value);
    setMovementPage(1);
  }, []);

  const updateMergeFilters: typeof setMergeFilters = useCallback((value) => {
    setMergeFilters(value);
    setMergePage(1);
  }, []);

  const openMovementDetail = (index: number) => setSelectedMovementIndex(index);
  const closeMovementDetail = () => setSelectedMovementIndex(null);
  const prevMovementDetail = () => setSelectedMovementIndex((i) => (i !== null && i > 0 ? i - 1 : i));
  const nextMovementDetail = () => setSelectedMovementIndex((i) => (i !== null && i < movementRows.length - 1 ? i + 1 : i));

  const openMergeDetail = (index: number) => setSelectedMergeIndex(index);
  const closeMergeDetail = () => setSelectedMergeIndex(null);
  const prevMergeDetail = () => setSelectedMergeIndex((i) => (i !== null && i > 0 ? i - 1 : i));
  const nextMergeDetail = () => setSelectedMergeIndex((i) => (i !== null && i < mergeRows.length - 1 ? i + 1 : i));

  const clearMovementFilters = () => {
    setMovementFilters({ search: '', sessionName: '', performedByName: '', sourceTableName: '', targetTableName: '', transferKind: '', dateFrom: '', dateTo: '' });
    setMovementPage(1);
  };

  const clearMergeFilters = () => {
    setMergeFilters({ search: '', sessionName: '', performedByName: '', tableName: '', kind: '', dateFrom: '', dateTo: '' });
    setMergePage(1);
  };

  const clearKitchenPrintFilters = () => {
    setKitchenCompanyFilter([]);
    setKitchenSessionFilter([]);
    setKitchenOrderFilter([]);
  };

  const toggleSessionSort = (nextKey: AdminSessionsSortKey) => {
    if (nextKey === sessionSortKey) {
      setSessionSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSessionSortKey(nextKey);
    setSessionSortDirection('asc');
  };

  const clearSessionFilters = () => {
    setSessionSessionFilter([]);
    setSessionStatusFilter([]);
    setSessionDateFilterMode('all');
    setSessionExactDate('');
    setSessionDateFrom('');
    setSessionDateTo('');
  };

  const clearApprovalRequestFilters = () => {
    setApprovalRequestSearch('');
    setApprovalRequestSearchFields([]);
    setApprovalRequestSessionFilter([]);
    setApprovalRequestActionTypeFilter([]);
    setApprovalRequestReasonFilter([]);
    setApprovalRequestStatusFilter([]);
    setApprovalRequestDateFrom('');
    setApprovalRequestDateTo('');
    closeApprovalRequestDetail();
  };

  const toggleKdsAuditSort = (nextKey: AdminKdsAuditSortKey) => {
    if (nextKey === kdsAuditSortKey) {
      setKdsAuditSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setKdsAuditSortKey(nextKey);
    setKdsAuditSortDirection('asc');
  };

  const clearKdsAuditFilters = () => {
    setKdsAuditCompanyFilter([]);
    setKdsAuditKitchenStatusFilter([]);
    setKdsAuditOrderTypeFilter([]);
    setKdsAuditOrderStatusFilter([]);
    setKdsAuditSessionFilter('');
    setKdsAuditSearch('');
    setKdsAuditDateFilterMode('all');
    setKdsAuditExactDate('');
    setKdsAuditDateFrom('');
    setKdsAuditDateTo('');
  };

  // Guard against stale responses when rows are clicked quickly ג€” only the latest request wins.
  const kdsHistoryReqRef = useRef(0);
  const openKdsHistoryModal = async (row: AdminKdsAuditView) => {
    const reqId = (kdsHistoryReqRef.current += 1);
    setKdsHistoryTarget(row);
    setIsKdsHistoryLoading(true);
    setKdsHistory(null);
    try {
      const history = row.selectionId != null
        ? await adminKdsAuditService.selectionHistory(row.selectionId)
        : await adminKdsAuditService.history(row.orderlineId);
      if (reqId === kdsHistoryReqRef.current) setKdsHistory(history);
    } catch (historyError: unknown) {
      if (reqId === kdsHistoryReqRef.current) {
        setError(historyError instanceof Error ? historyError.message : 'Failed to load history');
      }
    } finally {
      if (reqId === kdsHistoryReqRef.current) setIsKdsHistoryLoading(false);
    }
  };

  const closeKdsHistoryModal = () => setKdsHistoryTarget(null);

  return {
    activeSection,
    setActiveSection,
    ordersRows,
    ordersTotal,
    ordersTotalPages,
    ordersPage,
    setOrdersPage,
    isOrdersLoading,
    hasLoadedOrders,
    ordersSearch,
    setOrdersSearch,
    ordersCompanyFilter,
    setOrdersCompanyFilter,
    ordersSessionFilter,
    setOrdersSessionFilter,
    ordersStatusFilter,
    setOrdersStatusFilter,
    ordersAmountFilterMode,
    setOrdersAmountFilterMode,
    ordersAmountExact,
    setOrdersAmountExact,
    ordersAmountMin,
    setOrdersAmountMin,
    ordersAmountMax,
    setOrdersAmountMax,
    ordersDateFrom,
    setOrdersDateFrom,
    ordersDateTo,
    setOrdersDateTo,
    ordersCompanyOptions,
    ordersSessionOptions,
    ordersStatusOptions,
    ordersSortKey,
    ordersSortDirection,
    toggleOrdersSort,
    clearOrdersFilters,
    selectedOrderIndex,
    selectedOrderDetail,
    isOrderDetailLoading,
    openOrderDetail,
    closeOrderDetail,
    prevOrderDetail,
    nextOrderDetail,
    paymentlines,
    total,
    totalPages,
    page,
    setPage,
    isLoading,
    hasLoaded,
    error,
    savingLineId,
    statusFilter,
    setStatusFilter,
    companyFilter,
    setCompanyFilter,
    sessionFilter,
    setSessionFilter,
    orderFilter,
    setOrderFilter,
    methodFilter,
    setMethodFilter,
    amountFilterMode,
    setAmountFilterMode,
    amountExact,
    setAmountExact,
    amountMin,
    setAmountMin,
    amountMax,
    setAmountMax,
    dateFilterMode,
    setDateFilterMode,
    exactDate,
    setExactDate,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    companyOptions,
    sessionOptions,
    orderOptions,
    clearFilters,
    sortKey,
    sortDirection,
    toggleSort,
    getDraftForLine,
    applyAndSave,
    openAuditModal,
    auditTarget,
    auditRows,
    isAuditLoading,
    closeAuditModal,
    movementRows,
    movementTotal,
    movementTotalPages,
    movementPage,
    setMovementPage,
    movementTab,
    setMovementTab: selectMovementTab,
    isMovementLoading,
    hasLoadedMovements,
    selectedMovementIndex,
    openMovementDetail,
    closeMovementDetail,
    prevMovementDetail,
    nextMovementDetail,
    movementFilters,
    setMovementFilters: updateMovementFilters,
    clearMovementFilters,
    mergeRows,
    mergeTotal,
    mergeTotalPages: Math.max(1, Math.ceil(mergeTotal / PAGE_SIZE)),
    mergePage,
    setMergePage,
    isMergeLoading,
    hasLoadedMerges,
    selectedMergeIndex,
    openMergeDetail,
    closeMergeDetail,
    prevMergeDetail,
    nextMergeDetail,
    mergeFilters,
    setMergeFilters: updateMergeFilters,
    clearMergeFilters,
    kitchenPrintRows,
    kitchenPrintTotal,
    kitchenPrintTotalPages,
    kitchenPrintPage,
    setKitchenPrintPage,
    isKitchenPrintLoading,
    hasLoadedKitchenPrints,
    kitchenCompanyFilter,
    setKitchenCompanyFilter,
    kitchenSessionFilter,
    setKitchenSessionFilter,
    kitchenOrderFilter,
    setKitchenOrderFilter,
    kitchenCompanyOptions,
    kitchenSessionOptions,
    kitchenOrderOptions,
    clearKitchenPrintFilters,
    sessionRows,
    sessionTotal,
    sessionTotalPages,
    sessionPage,
    setSessionPage,
    isSessionLoading,
    hasLoadedSessions,
    sessionSessionFilter,
    setSessionSessionFilter,
    sessionStatusFilter,
    setSessionStatusFilter,
    sessionDateFilterMode,
    setSessionDateFilterMode,
    sessionExactDate,
    setSessionExactDate,
    sessionDateFrom,
    setSessionDateFrom,
    sessionDateTo,
    setSessionDateTo,
    sessionSessionOptions,
    sessionSortKey,
    sessionSortDirection,
    toggleSessionSort,
    clearSessionFilters,
    approvalRequestRows,
    approvalRequestTotal,
    approvalRequestTotalPages,
    approvalRequestPage,
    setApprovalRequestPage,
    isApprovalRequestLoading,
    hasLoadedApprovalRequests,
    selectedApprovalRequestIndex,
    selectedApprovalRequestDetail,
    isApprovalRequestDetailLoading,
    openApprovalRequestDetail,
    closeApprovalRequestDetail,
    prevApprovalRequestDetail,
    nextApprovalRequestDetail,
    approvalRequestFilterOptions,
    approvalRequestSearch,
    setApprovalRequestSearch,
    approvalRequestSearchFields,
    setApprovalRequestSearchFields,
    approvalRequestSessionFilter,
    setApprovalRequestSessionFilter,
    approvalRequestActionTypeFilter,
    setApprovalRequestActionTypeFilter,
    approvalRequestReasonFilter,
    setApprovalRequestReasonFilter,
    approvalRequestStatusFilter,
    setApprovalRequestStatusFilter,
    approvalRequestDateFrom,
    setApprovalRequestDateFrom,
    approvalRequestDateTo,
    setApprovalRequestDateTo,
    clearApprovalRequestFilters,
    kdsAuditRows,
    kdsAuditTotal,
    kdsAuditTotalPages,
    kdsAuditPage,
    setKdsAuditPage,
    isKdsAuditLoading,
    hasLoadedKdsAudit,
    kdsAuditCompanyFilter,
    setKdsAuditCompanyFilter,
    kdsAuditKitchenStatusFilter,
    setKdsAuditKitchenStatusFilter,
    kdsAuditOrderTypeFilter,
    setKdsAuditOrderTypeFilter,
    kdsAuditOrderStatusFilter,
    setKdsAuditOrderStatusFilter,
    kdsAuditSessionFilter,
    setKdsAuditSessionFilter,
    kdsAuditSessionOptions,
    kdsAuditSearch,
    setKdsAuditSearch,
    kdsAuditDateFilterMode,
    setKdsAuditDateFilterMode,
    kdsAuditExactDate,
    setKdsAuditExactDate,
    kdsAuditDateFrom,
    setKdsAuditDateFrom,
    kdsAuditDateTo,
    setKdsAuditDateTo,
    kdsAuditCompanyOptions,
    kdsAuditSortKey,
    kdsAuditSortDirection,
    toggleKdsAuditSort,
    clearKdsAuditFilters,
    getKdsDraftForLine,
    applyAndSaveKdsStatus,
    savingKdsRowKey,
    kdsRowKey,
    kdsHistoryTarget,
    kdsHistory,
    isKdsHistoryLoading,
    openKdsHistoryModal,
    closeKdsHistoryModal,
    errorRows,
    errorTotal,
    errorTotalPages,
    errorPage,
    setErrorPage,
    isErrorLoading,
    hasLoadedErrors,
    errorPlaceFilter,
    setErrorPlaceFilter,
    errorLevelFilter,
    setErrorLevelFilter,
    errorTenantFilter,
    setErrorTenantFilter,
    errorTenantOptions,
    errorSearch,
    setErrorSearch,
    errorDateFilterMode,
    setErrorDateFilterMode,
    errorExactDate,
    setErrorExactDate,
    errorDateFrom,
    setErrorDateFrom,
    errorDateTo,
    setErrorDateTo,
    errorStatusClassFilter,
    setErrorStatusClassFilter,
    errorIgnoredFilter,
    setErrorIgnoredFilter,
    clearErrorFilters,
    selectedError,
    openErrorDetail,
    closeErrorDetail,
    ignoreSelectedError,
    unignoreSelectedError,
    isSavingIgnore,

    restigoRows,
    restigoTotal,
    restigoPage,
    setRestigoPage,
    restigoTotalPages,
    isRestigoLoading,
    hasLoadedRestigo,
    restigoTenantFilter,
    setRestigoTenantFilter,
    restigoTenantOptions,
    restigoKindFilter,
    setRestigoKindFilter,
    restigoStatusFilter,
    setRestigoStatusFilter,
    restigoSearch,
    setRestigoSearch,
    restigoDateFilterMode,
    setRestigoDateFilterMode,
    restigoExactDate,
    setRestigoExactDate,
    restigoDateFrom,
    setRestigoDateFrom,
    restigoDateTo,
    setRestigoDateTo,
    clearRestigoFilters,
    selectedRestigo,
    openRestigoDetail,
    closeRestigoDetail,
    resendRestigo,
    isResendingRestigo,
    ontopoRows,
    ontopoTotal,
    ontopoTotalPages,
    ontopoPage,
    setOntopoPage,
    isOntopoLoading,
    hasLoadedOntopo,
    ontopoTenantFilter,
    setOntopoTenantFilter,
    ontopoTenantOptions,
    ontopoMethodFilter,
    setOntopoMethodFilter,
    ontopoStatusFilter,
    setOntopoStatusFilter,
    ontopoSearch,
    setOntopoSearch,
    ontopoDateFilterMode,
    setOntopoDateFilterMode,
    ontopoExactDate,
    setOntopoExactDate,
    ontopoDateFrom,
    setOntopoDateFrom,
    ontopoDateTo,
    setOntopoDateTo,
    clearOntopoFilters,
    selectedOntopoLog,
    openOntopoDetail,
    closeOntopoDetail,
    woltRows,
    woltTotal,
    woltTotalPages,
    woltPage,
    setWoltPage,
    isWoltLoading,
    hasLoadedWolt,
    woltTenantFilter,
    setWoltTenantFilter,
    woltTenantOptions,
    woltDirectionFilter,
    setWoltDirectionFilter,
    woltKindFilter,
    setWoltKindFilter,
    woltStatusFilter,
    setWoltStatusFilter,
    woltSearch,
    setWoltSearch,
    woltDateFilterMode,
    setWoltDateFilterMode,
    woltExactDate,
    setWoltExactDate,
    woltDateFrom,
    setWoltDateFrom,
    woltDateTo,
    setWoltDateTo,
    clearWoltFilters,
    selectedWoltLog,
    openWoltDetail,
    closeWoltDetail,
    tenbisRows,
    tenbisTotal,
    tenbisTotalPages,
    tenbisPage,
    setTenbisPage,
    isTenbisLoading,
    hasLoadedTenbis,
    tenbisTenantFilter,
    setTenbisTenantFilter,
    tenbisTenantOptions,
    tenbisDirectionFilter,
    setTenbisDirectionFilter,
    tenbisKindFilter,
    setTenbisKindFilter,
    tenbisStatusFilter,
    setTenbisStatusFilter,
    tenbisSearch,
    setTenbisSearch,
    tenbisDateFilterMode,
    setTenbisDateFilterMode,
    tenbisExactDate,
    setTenbisExactDate,
    tenbisDateFrom,
    setTenbisDateFrom,
    tenbisDateTo,
    setTenbisDateTo,
    clearTenbisFilters,
    selectedTenbisLog,
    openTenbisDetail,
    closeTenbisDetail,
    cibusRows,
    cibusTotal,
    cibusTotalPages,
    cibusPage,
    setCibusPage,
    isCibusLoading,
    hasLoadedCibus,
    cibusTenantFilter,
    setCibusTenantFilter,
    cibusTenantOptions,
    cibusDirectionFilter,
    setCibusDirectionFilter,
    cibusKindFilter,
    setCibusKindFilter,
    cibusStatusFilter,
    setCibusStatusFilter,
    cibusSearch,
    setCibusSearch,
    cibusDateFilterMode,
    setCibusDateFilterMode,
    cibusExactDate,
    setCibusExactDate,
    cibusDateFrom,
    setCibusDateFrom,
    cibusDateTo,
    setCibusDateTo,
    clearCibusFilters,
    selectedCibusLog,
    openCibusDetail,
    closeCibusDetail,
  };
};

export type UseMonitoringPage = ReturnType<typeof useMonitoringPage>;
